import React from 'react';
import FilterSelect, { FilterOption } from './FilterSelect';
import { RefreshCw } from 'lucide-react';
import { cn } from '../../utils/cn';

// 文件大小选项（替换来源选项）
const SIZE_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部大小' },
    { value: 'tiny', label: '小于1MB' },
    { value: 'small', label: '1-10MB' },
    { value: 'medium', label: '10-100MB' },
    { value: 'large', label: '大于100MB' },
];

// 文件夹类型选项
const FOLDER_TYPE_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部类型' },
    { value: 'public', label: '公开' },
    { value: 'restricted_public', label: '受限' },
    { value: 'private', label: '私密' },
];

// 是否收藏选项
const STARRED_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部文件' },
    { value: 'starred', label: '已收藏' },
    { value: 'unstarred', label: '未收藏' },
];

// 时间选项
const TIME_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部时间' },
    { value: 'today', label: '今天' },
    { value: 'week', label: '最近7天' },
    { value: 'month', label: '最近30天' },
    { value: 'year', label: '今年' },
];

// 格式选项（文档）
const FORMAT_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部格式' },
    { value: 'word', label: 'Word文档' },
    { value: 'excel', label: 'Excel表格' },
    { value: 'ppt', label: 'PPT演示' },
    { value: 'pdf', label: 'PDF文档' },
    { value: 'code', label: '代码文件' },
    { value: 'txt', label: '文本文件' },
];

// 清晰度选项（视频） - 基于文件名关键词判断
const RESOLUTION_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部分辨率' },
    { value: '4k', label: '4K' },
    { value: '1080p', label: '1080P' },
    { value: '720p', label: '720P' },
    { value: 'other', label: '其他' },
];


// 图片格式选项（替换媒体类型）
const IMAGE_FORMAT_OPTIONS: FilterOption[] = [
    { value: 'all', label: '全部格式' },
    { value: 'png', label: 'PNG' },
    { value: 'jpg', label: 'JPG/JPEG' },
    { value: 'gif', label: 'GIF' },
    { value: 'webp', label: 'WebP' },
    { value: 'other', label: '其他' },
];


interface FileFiltersProps {
    type: 'all' | 'recent' | 'video' | 'image' | 'document';
    filters: {
        size?: string;
        starred?: string;
        time?: string;
        format?: string;
        resolution?: string;
        imageFormat?: string;
        folderType?: string;
    };
    onFilterChange: (key: string, value: string) => void;
    onRefresh?: () => void;
    className?: string;
}

/**
 * 文件筛选器栏 - 仿夸克网盘设计
 * 根据不同Tab类型显示不同的筛选器
 */
const FileFilters: React.FC<FileFiltersProps> = ({
    type,
    filters,
    onFilterChange,
    onRefresh,
    className,
}) => {
    // 获取当前Tab应该显示的筛选器
    const getFiltersForType = () => {
        switch (type) {
            case 'video':
                return (
                    <>
                        <FilterSelect
                            value={filters.size || 'all'}
                            options={SIZE_OPTIONS}
                            onChange={(v) => onFilterChange('size', v)}
                        />
                        <FilterSelect
                            value={filters.resolution || 'all'}
                            options={RESOLUTION_OPTIONS}
                            onChange={(v) => onFilterChange('resolution', v)}
                        />
                        <FilterSelect
                            value={filters.starred || 'all'}
                            options={STARRED_OPTIONS}
                            onChange={(v) => onFilterChange('starred', v)}
                        />
                    </>
                );
            case 'image':
                return (
                    <>
                        <FilterSelect
                            value={filters.size || 'all'}
                            options={SIZE_OPTIONS}
                            onChange={(v) => onFilterChange('size', v)}
                        />
                        <FilterSelect
                            value={filters.imageFormat || 'all'}
                            options={IMAGE_FORMAT_OPTIONS}
                            onChange={(v) => onFilterChange('imageFormat', v)}
                        />
                        <FilterSelect
                            value={filters.starred || 'all'}
                            options={STARRED_OPTIONS}
                            onChange={(v) => onFilterChange('starred', v)}
                        />
                    </>
                );
            case 'document':
                return (
                    <>
                        <FilterSelect
                            value={filters.format || 'all'}
                            options={FORMAT_OPTIONS}
                            onChange={(v) => onFilterChange('format', v)}
                        />
                        <FilterSelect
                            value={filters.size || 'all'}
                            options={SIZE_OPTIONS}
                            onChange={(v) => onFilterChange('size', v)}
                        />
                        <FilterSelect
                            value={filters.starred || 'all'}
                            options={STARRED_OPTIONS}
                            onChange={(v) => onFilterChange('starred', v)}
                        />
                    </>
                );
            case 'all':
                // "全部"tab：显示文件夹类型筛选
                return (
                    <>
                        <FilterSelect
                            value={filters.time || 'all'}
                            options={TIME_OPTIONS}
                            onChange={(v) => onFilterChange('time', v)}
                        />
                        <FilterSelect
                            value={filters.folderType || 'all'}
                            options={FOLDER_TYPE_OPTIONS}
                            onChange={(v) => onFilterChange('folderType', v)}
                        />
                        <FilterSelect
                            value={filters.starred || 'all'}
                            options={STARRED_OPTIONS}
                            onChange={(v) => onFilterChange('starred', v)}
                        />
                    </>
                );
            default:
                // recent 或其他
                return (
                    <>
                        <FilterSelect
                            value={filters.time || 'all'}
                            options={TIME_OPTIONS}
                            onChange={(v) => onFilterChange('time', v)}
                        />
                        <FilterSelect
                            value={filters.size || 'all'}
                            options={SIZE_OPTIONS}
                            onChange={(v) => onFilterChange('size', v)}
                        />
                        <FilterSelect
                            value={filters.starred || 'all'}
                            options={STARRED_OPTIONS}
                            onChange={(v) => onFilterChange('starred', v)}
                        />
                    </>
                );
        }
    };

    return (
        <div className={cn('flex items-center gap-2', className)}>
            {getFiltersForType()}
        </div>
    );
};

export default FileFilters;
